import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import {
  ADMIN_PAY_METHOD_LABEL, IgnoreReason, ModuleKey, PermissionLevel, hasAtLeast,
  TAX_KINDS as SHARED_TAX_KINDS, TAX_KIND_LABEL as SHARED_TAX_KIND_LABEL,
  TAX_SOURCE_LABEL as SHARED_TAX_SOURCE_LABEL, type TaxSource as SharedTaxSource,
} from '@masamune/shared';

/**
 * Amministrazione — tipi, query options e helper condivisi tra AdminPage
 * (tab), la pagina Oggi (card + stream) e le Impostazioni (card Qonto).
 *
 * API: /api/v1/admin (apps/api/src/modules/admin). Gli importi arrivano già
 * come numeri, le date contabili come "YYYY-MM-DD". Dove il DTO dell'API è
 * più ricco (o diverso) del contratto iniziale, i `normalize*` qui sotto
 * portano tutto a UNA forma per la UI. Query keys: prefisso ['admin', ...].
 *
 * Storico: di default l'API mostra solo i documenti dalla data di taglio in poi
 * (OrgSettings.adminCutoffDate, 1/1/2026). `includeHistory` accende l'interruttore
 * "mostra anche gli anni scorsi" ed entra sia nell'URL sia nella queryKey, così le
 * due viste hanno cache separate e la pagina Oggi resta sempre sul presente.
 */

/** `&includeHistory=true` quando l'interruttore dello storico è acceso. */
function withHistory(sp: URLSearchParams, includeHistory?: boolean): URLSearchParams {
  if (includeHistory) sp.set('includeHistory', 'true');
  return sp;
}

// ── Overview ────────────────────────────────────────────────────────────────

export type AccountProvider = 'QONTO' | 'CASH';
export interface AdminAccount {
  id: string;
  provider: AccountProvider;
  name: string;
  balance: number;
  balanceAt: string | null;
  iban?: string | null;
  openingBalance?: number;
  main?: boolean;
  status?: string | null;
  lastSyncedAt?: string | null;
}

export type TaxKind =
  | 'F24_DIPENDENTI' | 'F24_IRPEF' | 'IRES_IRAP' | 'INPS' | 'IVA_TRIMESTRALE'
  | 'IVA_ARRETRATA' | 'ADER' | 'CAMERALE' | 'ALTRO';
/** Da dove viene la cifra di una tassa: il tipo vive in @masamune/shared, con CALCOLATO (calcolata da noi). */
export type TaxSource = SharedTaxSource;
export type Entity = 'MASAMUNE' | 'PERSONALE_ANDREA';

/** Prossima scadenza fiscale (una rata): l'API dà gli id di rata e obbligo. */
export interface TaxNext {
  installmentId?: string;
  obligationId?: string;
  kind: TaxKind;
  description: string;
  period?: string | null;
  dueDate: string;
  amount: number;
  source: TaxSource;
  overdueDays?: number;
  entity?: Entity;
}

/**
 * Rata aperta con un movimento Qonto compatibile.
 *
 * Arriva dalla riconciliazione, quindi porta con sé quanto è solida la proposta
 * e la frase che lo spiega: la card della Panoramica mostra solo le prime dieci,
 * il quadro intero (uscite comprese) sta nella tab "Da riconciliare".
 */
export interface ToReview {
  paymentId: string;
  invoiceId?: string;
  invoiceNumber: string;
  clientName: string;
  amount: number;
  dueDate: string | null;
  certezza?: Certezza;
  motivo?: string;
  transaction: { id: string; settledOn: string; label: string; counterparty: string | null; accountId?: string | null };
}

/**
 * Da dove vengono i numeri: un aggiornamento (Qonto o FattureInCloud) come lo
 * racconta l'API (admin.situazione.ts, `statoSync`). Arriva sia in
 * /admin/situazione sia dentro l'overview (`sync.qonto`, `sync.fic`).
 */
export interface StatoSync {
  provider: 'QONTO' | 'FIC';
  /** "Qonto", "FattureInCloud". */
  nome: string;
  collegato: boolean;
  ultimoOk: string | null;
  ultimoTentativo: string | null;
  esito: 'OK' | 'FAILED' | 'RUNNING' | null;
  errore: string | null;
  fallitiUltime24h: number;
  /** L'ultimo errore delle 24 ore, anche se poi un aggiornamento è riuscito. */
  erroreUltime24h: string | null;
  /** Vero se l'ultimo tentativo è fallito o se i dati sono fermi da troppo. */
  problema: boolean;
  /** In parole, senza il nome: "ultimo aggiornamento alle 10:45…". */
  testo: string;
  /** Il problema detto con il soggetto: "FattureInCloud non si aggiorna da ieri alle 7:22". */
  testoProblema: string | null;
}

/** La forma di prima (connected, lastAt, status, error) più i campi di `statoSync`. */
export interface SyncState extends StatoSync { connected?: boolean; lastAt: string | null; status: string | null; error?: string | null }

export type ParteScaduti = 'fisco' | 'fornitori' | 'speseFisse' | 'stipendi' | 'contanti';

/**
 * "Pagamenti scaduti non ancora fatti" (admin.situazione.ts, `calcolaScaduti`):
 * fuori dai mesi della previsione (R4). La casa del numero è la Situazione.
 */
export interface Scaduti {
  totale: number;
  fisco: { importo: number; rate: number; provvisorio: number; iva: { importo: number; trimestri: number; dal: number | null } };
  fornitori: { importo: number; documenti: number };
  speseFisse: { importo: number; occorrenze: number };
  stipendi: { importo: number; occorrenze: number };
  contanti: { importo: number; voci: number };
  /** In parole, senza importi: "IVA non versata di 9 trimestri, 4 fatture di fornitori e 5 spese fisse". */
  descrizione: string;
  /** La parte più grande: decide in quale pagina porta il clic sul numero. */
  principale: ParteScaduti | null;
}

/** Un bonifico che paga con certezza una fattura di un fornitore (da confermare in Banca). */
export interface AbbinamentoSicuroUscita {
  rataId: string;
  docId: string;
  docNumero: string | null;
  controparte: string;
  importo: number;
  dueDate: string | null;
  movimento: { id: string; settledOn: string | null; label: string; counterparty: string | null };
}

/** Una scadenza fiscale dei prossimi 30 giorni che a sistema non ha ancora la cifra. */
export interface CifraMancante {
  installmentId: string;
  obligationId: string;
  kind: string;
  description: string;
  dueDate: string | null;
  overdueDays: number;
}

export interface AdminOverview {
  today?: string;
  /** Da che data in poi sono i numeri qui sotto (null = si sta guardando tutto). */
  history?: { cutoffDate: string | null; includeHistory: boolean };
  accounts: AdminAccount[];
  liquidity: { bank: number; cash: number; total: number };
  incassi: {
    thisMonth: { expected: number; received: number; open: number; overdue: number };
    nextMonth: { expected: number };
    next30: { expected: number; overdue?: number };
  };
  uscite: { thisMonth: { expected: number; paid: number; open: number; overdue?: number; openNoTax?: number; overdueNoTax?: number }; nextMonth: { expected: number } };
  /** `count` = documenti aperti, `vendorCount` = fornitori distinti (quello che vede la tab). */
  fornitori: { open: number; overdue: number; count: number; vendorCount?: number };
  tasse: { openTotal: number; overdue?: number; next: TaxNext[] };
  iva: { byYear: Array<{ year: number; due: number; paid: number; residual: number; overdue?: number; upcoming?: number; source: TaxSource; id?: string }>; residualTotal: number; overdueTotal?: number; upcomingTotal?: number };
  fatture: {
    openCount: number;
    openAmount: number;
    overdueCount: number;
    overdueAmount: number;
    /** Solo le proposte CERTE: qui "Conferma" è un clic solo, senza la prova sotto gli occhi. */
    toReview: ToReview[];
    /** Quanto resta da riconciliare in tutto, dubbi compresi: è il numero del pallino sulla tab. */
    daRiconciliare?: { proposte: number; certe: number; valoreCerte: number; agganci: number };
  };
  /** "Pagamenti scaduti non ancora fatti": fuori da `incassi`/`uscite` del mese (R4). */
  scaduti: Scaduti;
  /**
   * Le cose da fare che non sono né rate né tasse in scadenza: i bonifici che
   * pagano con certezza una fattura di un fornitore e le scadenze fiscali
   * senza cifra. Le legge "Cose da fare oggi".
   */
  daFare: { abbinamentiSicuriUscite: AbbinamentoSicuroUscita[]; cifreMancanti: CifraMancante[] };
  sync: { qonto: SyncState; fic: SyncState };
}

/** Accetta sia la forma dell'API ({ transaction }) sia quella del contratto iniziale (transactionId…). */
function normalizeReview(t: any): ToReview {
  const tx = t?.transaction ?? { id: t?.transactionId, settledOn: t?.settledAt, label: t?.transactionLabel, counterparty: null };
  return {
    paymentId: t.paymentId,
    invoiceId: t.invoiceId,
    invoiceNumber: t.invoiceNumber ?? 's/n',
    clientName: t.clientName ?? t.client ?? '',
    amount: Number(t.amount) || 0,
    dueDate: t.dueDate ?? null,
    certezza: t.certezza ?? undefined,
    motivo: t.motivo ?? undefined,
    transaction: {
      id: tx?.id ?? '',
      settledOn: String(tx?.settledOn ?? tx?.settledAt ?? '').slice(0, 10),
      label: tx?.label ?? '',
      counterparty: tx?.counterparty ?? null,
      accountId: tx?.accountId ?? null,
    },
  };
}

export function adminOverviewQueryFor(includeHistory = false) {
  const sp = withHistory(new URLSearchParams(), includeHistory);
  const qs = sp.toString();
  return {
    queryKey: ['admin', 'overview', includeHistory] as const,
    queryFn: async (): Promise<AdminOverview> => {
      const r = await api.get<any>(`/admin/overview${qs ? `?${qs}` : ''}`);
      return { ...r, fatture: { ...r.fatture, toReview: (r.fatture?.toReview ?? []).map(normalizeReview) } };
    },
    // Alimenta Oggi (card + stream), Sidebar e la pagina: un fetch al minuto basta.
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  };
}

/** Overview "del presente": è quella che vedono Oggi, Sidebar e la dashboard. */
export const adminOverviewQuery = adminOverviewQueryFor(false);

// ── Cash forecast ───────────────────────────────────────────────────────────

export interface ForecastBucket {
  month: string; // YYYY-MM
  in: { invoices: number; cash: number; total: number };
  out: { payables: number; recurring: number; taxes: number; cash: number; total: number };
  net: number;
  cumulative: number;
}
/** L'API risponde { start: { bank, cash, total }, months: [...] }: qui start = totale, buckets = mesi. */
export interface CashForecast { start: number; buckets: ForecastBucket[] }

export function cashForecastQuery(from: string, to: string, includeHistory?: boolean) {
  const qs = withHistory(new URLSearchParams({ from, to }), includeHistory).toString();
  return {
    queryKey: ['admin', 'cash-forecast', from, to, Boolean(includeHistory)] as const,
    queryFn: async (): Promise<CashForecast> => {
      const r = await api.get<any>(`/admin/cash-forecast?${qs}`);
      return {
        start: typeof r.start === 'number' ? r.start : Number(r.start?.total ?? 0),
        buckets: r.buckets ?? r.months ?? [],
      };
    },
    staleTime: 60_000,
  };
}

// ── Incassi (rate delle fatture emesse) ─────────────────────────────────────

// ── Cestino ─────────────────────────────────────────────────────────────────

/**
 * Cestinare = togliere un documento dal gestionale. NON annulla niente su
 * FattureInCloud e non è un'operazione fiscale: una fattura davvero emessa si
 * annulla con una nota di credito, che fa il commercialista. Serve per i
 * documenti che nel gestionale non devono esserci (scartati dallo SdI,
 * doppioni, errori): spariscono dalle liste e dai totali, restano nel cestino
 * con motivo e data e da lì si ripristinano. Il sync FiC non li riporta su.
 */
export type IgnoreReasonKey = (typeof IgnoreReason)[keyof typeof IgnoreReason];
export const IGNORE_REASONS: IgnoreReasonKey[] = [
  IgnoreReason.SCARTATA_SDI, IgnoreReason.DOPPIONE, IgnoreReason.ERRORE, IgnoreReason.ANNULLATA_FIC, IgnoreReason.ALTRO,
];

/** Campi che l'API aggiunge alle righe del cestino. */
export interface IgnoredFields {
  ignoredReason?: IgnoreReasonKey | null;
  ignoredNote?: string | null;
  ignoredAt?: string | null;
}

/** STORNATA = `reversed` su FiC: fuori dai crediti, compare solo con status=tutte. */
export type IncassoStatus = 'DA_INCASSARE' | 'INCASSATA' | 'STORNATA' | 'CESTINATA';
export type PayMethod = 'BONIFICO' | 'CONTANTI' | 'STRIPE' | 'CARTA' | 'ADDEBITO' | 'RIBA' | 'COMPENSAZIONE' | 'ALTRO';

export interface IncassoRow extends IgnoredFields {
  id: string;            // paymentId (nel cestino: l'id della fattura)
  invoiceId: string;
  invoiceNumber: string;
  invoiceType?: string;
  invoiceStatus?: string;
  client: { id: string; shortName: string };
  seq: number;
  /** Numero di rate della fattura; 0 = l'API non lo dice. */
  ofTotal: number;
  dueDate: string | null;
  amount: number;
  status: IncassoStatus;
  paidDate: string | null;
  method: PayMethod | null;
  overdueDays: number;
  bankTransactionId?: string | null;
  ficUrl?: string | null;
  /** Valorizzato = chiusa qui, su FattureInCloud risulta ancora aperta. */
  localPaidAt?: string | null;
  localPaidNote?: string | null;
}

export interface Paginated<T> { items: T[]; page: number; pageSize: number; total: number; totalPages: number }

function normalizeIncasso(r: any): IncassoRow {
  return {
    ...r,
    invoiceNumber: r.invoiceNumber ?? 's/n',
    client: r.client ?? { id: r.clientId ?? '', shortName: r.clientName ?? '' },
    ofTotal: Number(r.ofTotal ?? r.installmentsCount ?? 0),
    amount: Number(r.amount) || 0,
    overdueDays: Number(r.overdueDays) || 0,
  };
}

// `cestinate` è il cestino: una riga per FATTURA (non per rata), con motivo e data.
export type IncassiFilter = 'aperte' | 'incassate' | 'tutte' | 'cestinate';
export interface IncassiParams { status: IncassiFilter; from?: string; to?: string; clientId?: string; search?: string; includeHistory?: boolean; page?: number; pageSize?: number; /** Ordina per, solo per le incassate (24/9). */ sort?: string }

export function incassiQuery(p: IncassiParams) {
  const sp = new URLSearchParams();
  sp.set('status', p.status);
  if (p.from) sp.set('from', p.from);
  if (p.to) sp.set('to', p.to);
  if (p.clientId) sp.set('clientId', p.clientId);
  if (p.search?.trim()) sp.set('search', p.search.trim());
  if (p.sort && p.sort !== 'recenti') sp.set('sort', p.sort);
  withHistory(sp, p.includeHistory);
  sp.set('page', String(p.page ?? 1));
  sp.set('pageSize', String(p.pageSize ?? 25));
  return {
    queryKey: ['admin', 'incassi', p] as const,
    queryFn: async (): Promise<Paginated<IncassoRow>> => {
      const r = await api.get<Paginated<any>>(`/admin/incassi?${sp.toString()}`);
      return { ...r, items: (r.items ?? []).map(normalizeIncasso) };
    },
    staleTime: 15_000,
  };
}

/**
 * Rate aperte in scadenza nei prossimi 30 giorni (comprese le scadute).
 * Una sola query condivisa tra stream Oggi (che tiene i 7 giorni), card
 * "Prossime scadenze" e Sidebar: cache unica, un fetch al minuto.
 */
export function upcomingRateQuery() {
  return {
    ...incassiQuery({ status: 'aperte', to: isoDayPlus(30), pageSize: 100 }),
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  };
}

// ── Uscite (fornitori + ricorrenti + tasse + contanti) ──────────────────────

export type UscitaSource = 'PAYABLE' | 'RECURRING' | 'TAX' | 'CASH';
export type UsciteFilter = 'aperte' | 'pagate' | 'tutte';

/**
 * Riga unificata. `id` è ciò che POST /uscite/pay si aspetta: rata fornitore
 * (PAYABLE), ricorrente (RECURRING, con `dueDate` dell'occorrenza), rata
 * fiscale (TAX), voce di cassa (CASH). `refId` = documento/obbligo padre.
 */
export interface UscitaRow {
  source: UscitaSource;
  id: string;
  refId?: string;
  counterparty: string;
  description: string | null;
  dueDate: string | null;
  amount: number;
  status: 'DA_PAGARE' | 'PAGATA' | string;
  paidDate?: string | null;
  method?: PayMethod | null;
  entity: Entity;
  overdueDays?: number;
  bankTransactionId?: string | null;
}

export interface UsciteParams { status: UsciteFilter; from?: string; to?: string; search?: string; includeHistory?: boolean; page?: number; pageSize?: number }

export function usciteQuery(p: UsciteParams) {
  const sp = new URLSearchParams();
  sp.set('status', p.status);
  if (p.from) sp.set('from', p.from);
  if (p.to) sp.set('to', p.to);
  if (p.search?.trim()) sp.set('search', p.search.trim());
  withHistory(sp, p.includeHistory);
  sp.set('page', String(p.page ?? 1));
  sp.set('pageSize', String(p.pageSize ?? 25));
  return {
    queryKey: ['admin', 'uscite', p] as const,
    queryFn: () => api.get<Paginated<UscitaRow>>(`/admin/uscite?${sp.toString()}`),
    staleTime: 15_000,
  };
}

/** Come si abbina un movimento Qonto a una riga uscite (RECURRING non ha un'entità: si paga l'occorrenza). */
export const SOURCE_TO_MATCH: Record<UscitaSource, MatchKind | null> = {
  PAYABLE: 'PAYABLE_PAYMENT', RECURRING: null, TAX: 'TAX_INSTALLMENT', CASH: 'CASH_ENTRY',
};

// ── Fornitori (fatture ricevute) ────────────────────────────────────────────

export interface PayablePayment {
  id: string;
  seq: number;
  dueDate: string | null;
  amount: number;
  status: 'DA_PAGARE' | 'PAGATA' | 'STORNATA' | string;
  paidDate: string | null;
  method: PayMethod | null;
  bankTransactionId?: string | null;
  /** Come IncassoRow.localPaidAt: pagata qui, su FattureInCloud ancora aperta. */
  localPaidAt?: string | null;
  localPaidNote?: string | null;
}

export interface PayableRow extends IgnoredFields {
  id: string;
  vendorName: string;
  supplierId?: string | null;
  number: string | null;
  kind?: string;
  issueDate: string | null;
  dueDate: string | null;
  total: number;
  openAmount?: number;
  paidAmount?: number;
  description?: string | null;
  status: string; // DA_PAGARE | PARZIALE | PAGATA | ANNULLATA | FINANZIATA
  entity?: Entity;
  overdueDays?: number;
  payments?: PayablePayment[];
  /** Solo sulle FINANZIATA: la rata ricorrente che sta pagando il documento. */
  recurringExpenseId?: string | null;
  recurringExpenseName?: string | null;
  recurringExpenseAmount?: number | null;
}

/**
 * Fatture ricevute ancora da pagare. `finanziate` è la lista a parte delle auto
 * a rate: il fornitore è già stato pagato dalla finanziaria, quindi non sono
 * debito corrente e non entrano nei totali (la data di taglio non le tocca).
 */
export function payablesQuery(p: { status?: 'aperte' | 'finanziate' | 'cestinate'; includeHistory?: boolean } = {}) {
  const sp = new URLSearchParams({ status: p.status ?? 'aperte', pageSize: '500' });
  withHistory(sp, p.includeHistory);
  return {
    queryKey: ['admin', 'payables', p.status ?? 'aperte', Boolean(p.includeHistory)] as const,
    queryFn: async (): Promise<PayableRow[]> => {
      const r = await api.get<PayableRow[] | Paginated<PayableRow>>(`/admin/payables?${sp.toString()}`);
      return Array.isArray(r) ? r : (r?.items ?? []);
    },
    staleTime: 30_000,
  };
}

// ── Fisco ───────────────────────────────────────────────────────────────────

export interface TaxInstallment { id: string; seq: number; dueDate: string; amount: number; paidAt: string | null; method?: PayMethod | null; bankTransactionId?: string | null }
export interface TaxObligation {
  id: string;
  kind: TaxKind;
  year: number;
  period: string | null;
  description: string;
  amountDue: number;
  amountPaid?: number;
  residual?: number;
  dueDate: string | null;
  nextDueDate?: string | null;
  status: string;
  source: TaxSource;
  note: string | null;
  entity: Entity;
  overdueDays?: number;
  installments?: TaxInstallment[];
}

export const taxesQuery = {
  queryKey: ['admin', 'taxes'] as const,
  queryFn: async (): Promise<TaxObligation[]> => {
    const r = await api.get<TaxObligation[] | { items: TaxObligation[] }>('/admin/taxes');
    return Array.isArray(r) ? r : (r?.items ?? []);
  },
  staleTime: 30_000,
};

/** I tipi di tassa e i loro nomi: una lista sola, quella di @masamune/shared ("F24 IRPEF" e "IRES / IRAP" non si dicono più). */
export const TAX_KINDS: TaxKind[] = SHARED_TAX_KINDS;
export const TAX_KIND_LABEL: Record<TaxKind, string> = SHARED_TAX_KIND_LABEL;

// ── Ricorrenti ──────────────────────────────────────────────────────────────

export type Cycle = 'MONTHLY' | 'BIMONTHLY' | 'QUARTERLY' | 'YEARLY';
export const CYCLES: Cycle[] = ['MONTHLY', 'BIMONTHLY', 'QUARTERLY', 'YEARLY'];
export const CYCLE_LABEL: Record<Cycle, string> = { MONTHLY: 'Ogni mese', BIMONTHLY: 'Ogni due mesi', QUARTERLY: 'Ogni trimestre', YEARLY: 'Ogni anno' };

export interface RecurringExpense {
  id: string;
  name: string;
  counterparty: string | null;
  amountNet: number;
  vatRate: number;
  amountGross?: number;
  cycle: Cycle;
  dayOfMonth: number | null;
  startDate: string | null;
  endDate: string | null;
  nextDueDate: string | null;
  active: boolean;
  category: string | null;
  method: PayMethod | null;
  note: string | null;
}

// ── Movimenti Qonto ─────────────────────────────────────────────────────────

export type MatchKind = 'INVOICE_PAYMENT' | 'PAYABLE_PAYMENT' | 'TAX_INSTALLMENT' | 'CASH_ENTRY';

/** A cosa è collegato un movimento della banca, quando l'API non manda il nome: parole intere. */
export const MATCH_LABEL: Record<MatchKind, string> = {
  INVOICE_PAYMENT: 'Fattura di un cliente',
  PAYABLE_PAYMENT: 'Fattura di un fornitore',
  TAX_INSTALLMENT: 'Versamento al fisco',
  CASH_ENTRY: 'Voce scritta a mano',
};

export interface BankTransaction {
  id: string;
  account: { id: string; name: string };
  side: 'DEBIT' | 'CREDIT';
  amount: number;
  label: string;
  counterparty: string | null;
  operationType: string | null;
  category: string | null;
  /** Data valuta, "YYYY-MM-DD" (settledOn dell'API). */
  settledAt: string;
  matched: { kind: MatchKind; id: string; label: string } | null;
  /** Solo dalle suggestions: distanza in giorni dalla scadenza della rata. */
  distanceDays?: number;
}

/** DTO API → forma UI: conto composto, data valuta date-only, abbinamento con etichetta. */
export function normalizeTx(t: any): BankTransaction {
  const m = t?.matched ?? t?.match ?? null;
  return {
    ...t,
    account: t.account ?? { id: t.accountId ?? '', name: t.accountName ?? '' },
    amount: Number(t.amount) || 0,
    settledAt: t.settledOn ?? (t.settledAt ? String(t.settledAt).slice(0, 10) : ''),
    matched: m ? { kind: m.kind, id: m.id, label: m.label ?? MATCH_LABEL[m.kind as MatchKind] ?? 'Collegato a una voce' } : null,
  };
}

export interface TransactionsParams { accountId?: string; from?: string; to?: string; side?: 'DEBIT' | 'CREDIT' | ''; unmatched?: boolean; search?: string; includeHistory?: boolean; page?: number; pageSize?: number }

export function transactionsQuery(p: TransactionsParams) {
  const sp = new URLSearchParams();
  if (p.accountId) sp.set('accountId', p.accountId);
  if (p.from) sp.set('from', p.from);
  if (p.to) sp.set('to', p.to);
  if (p.side) sp.set('side', p.side);
  if (p.unmatched) sp.set('unmatched', 'true');
  if (p.search?.trim()) sp.set('search', p.search.trim());
  withHistory(sp, p.includeHistory);
  sp.set('page', String(p.page ?? 1));
  sp.set('pageSize', String(p.pageSize ?? 25));
  return {
    queryKey: ['admin', 'transactions', p] as const,
    queryFn: async (): Promise<Paginated<BankTransaction>> => {
      const r = await api.get<Paginated<any>>(`/admin/transactions?${sp.toString()}`);
      return { ...r, items: (r.items ?? []).map(normalizeTx) };
    },
    staleTime: 15_000,
  };
}

/** Movimenti Qonto non abbinati compatibili con una rata/voce (stesso importo, finestra di date). */
export function suggestionsQuery(target: { kind: MatchKind; id: string }) {
  return {
    queryKey: ['admin', 'transactions', 'suggestions', target.kind, target.id] as const,
    queryFn: async (): Promise<BankTransaction[]> => {
      const r = await api.get<any>(`/admin/transactions/suggestions?kind=${target.kind}&paymentId=${encodeURIComponent(target.id)}`);
      const items = Array.isArray(r) ? r : (r?.items ?? []);
      return items.map(normalizeTx);
    },
    staleTime: 30_000,
  };
}

// ── Chiuse qui (rate saldate in OS, su FattureInCloud ancora aperte) ────────

/**
 * Una rata che abbiamo segnato incassata/pagata dentro Masamune OS mentre su
 * FattureInCloud risulta ancora da saldare. È la lista di cosa andrà riportato
 * al commercialista: si svuota da sola quando FiC recupera il pagamento.
 */
export interface ChiusuraLocaleRow {
  kind: 'INCASSO' | 'USCITA';
  id: string;
  docId: string;
  docNumber: string | null;
  counterparty: string;
  seq: number;
  amount: number;
  dueDate: string | null;
  localPaidAt: string | null;
  localPaidNote: string | null;
  paidDate: string | null;
  transaction: { id: string; settledOn: string | null; amount: number; counterparty: string | null } | null;
  /** Il documento vive su FiC: senza, non c'è niente da riportare. */
  onFic: boolean;
}

export interface ChiusureLocaliPage extends Paginated<ChiusuraLocaleRow> {
  totals: { incassi: number; uscite: number; daRiportare: number };
}

export function chiusureLocaliQuery(p: { search?: string; page?: number; pageSize?: number } = {}) {
  const sp = new URLSearchParams();
  if (p.search?.trim()) sp.set('search', p.search.trim());
  sp.set('page', String(p.page ?? 1));
  sp.set('pageSize', String(p.pageSize ?? 50));
  return {
    queryKey: ['admin', 'chiusure-locali', p] as const,
    queryFn: () => api.get<ChiusureLocaliPage>(`/admin/chiusure-locali?${sp.toString()}`),
    staleTime: 15_000,
  };
}

// ── Riconciliazione (banca ↔ documenti) ─────────────────────────────────────

/**
 * Le proposte di abbinamento fra i movimenti Qonto e le rate dei documenti.
 *
 * `certezza` è la cosa da leggere per prima:
 *   CERTO     importo + nome + data, un solo movimento possibile → si chiude da solo
 *   PROBABILE due indizi su tre → si propone, decide una persona
 *   DUBBIO    solo l'importo, o più movimenti identici → non si chiude mai da solo
 *
 * `indizi` dice quali dei tre ci sono, così la riga si può giustificare senza
 * aprire niente. API: /admin/riconciliazione.
 */
export type Certezza = 'CERTO' | 'PROBABILE' | 'DUBBIO';
export type ForzaNome = 'FORTE' | 'DEBOLE' | 'NESSUNA';
export type LatoRata = 'INCASSO' | 'USCITA';

export interface MovimentoRic {
  id: string;
  settledOn: string | null;
  amount: number;
  counterparty: string | null;
  label: string;
  operationType: string | null;
  accountId: string;
  accountName: string | null;
}

export interface PropostaRic {
  key: string;
  lato: LatoRata;
  rataId: string;
  docId: string;
  docNumero: string | null;
  controparte: string;
  importo: number;
  dueDate: string | null;
  certezza: Certezza;
  indizi: { importo: boolean; nome: ForzaNome; data: boolean };
  motivo: string;
  gruppo: string | null;
  movimento: MovimentoRic;
  alternativi: number;
  /**
   * Da dove viene il dubbio: altre fatture che vogliono lo stesso bonifico
   * (ALTRE_RATE) o altri bonifici uguali (ALTRI_MOVIMENTI). null = nessuno.
   */
  ambiguita: AmbiguitaRic;
}

export type AmbiguitaRic = 'ALTRE_RATE' | 'ALTRI_MOVIMENTI' | null;

export interface FotografiaRic {
  incassi: { rate: number; totale: number };
  uscite: { rate: number; totale: number };
  suCestinate: { rate: number; totale: number };
  totaleAperto: number;
}

export interface RiconciliazioneData {
  /** Rate già chiuse che hanno ritrovato il loro movimento: agganciare non cambia nulla di stato. */
  agganci: PropostaRic[];
  /** Rate ancora aperte: queste, se certe, si chiudono. */
  aperte: PropostaRic[];
  scartate: PropostaRic[];
  /** `gonfiaIConti` distingue i soldi contati due volte (prestito, giroconti) da quelli veri ma non abbinabili (Stripe, commissioni). */
  rumore: Array<{ motivo: string; etichetta: string; movimenti: number; totale: number; gonfiaIConti: boolean }>;
  senzaDocumento: { movimenti: number; totale: number; perAnno: Array<{ anno: number; movimenti: number; entrate: number; uscite: number }> };
  fotografia: FotografiaRic;
  lotti: Array<{ lotto: string; quando: string; chiuse: number; agganciate: number; valore: number; annullato: boolean }>;
}

export const riconciliazioneQuery = {
  queryKey: ['admin', 'riconciliazione'] as const,
  queryFn: () => api.get<RiconciliazioneData>('/admin/riconciliazione'),
  staleTime: 30_000,
};

export interface DocumentoGrande {
  lato: LatoRata;
  docId: string;
  numero: string | null;
  controparte: string;
  totale: number;
  dataDocumento: string | null;
  stato: string;
  /** `buchi` = rate chiuse che dicono "bonifico" e in banca non ci sono. È il numero che conta. */
  rate: { previste: number; chiuse: number; conMovimento: number; buchi: number };
  aperto: number;
  diagnosi: string;
}

export const pagamentiGrandiQuery = {
  queryKey: ['admin', 'riconciliazione', 'grandi'] as const,
  queryFn: () => api.get<{ items: DocumentoGrande[] }>('/admin/riconciliazione/pagamenti-grandi'),
  staleTime: 60_000,
};

export interface RateMancantiRic {
  recurringId: string;
  nome: string;
  controparte: string | null;
  dalGestionale: string | null;
  dallaBanca: string | null;
  documenti: Array<{ numero: string | null; fornitore: string; totale: number }>;
  addebiti: number;
  totaleAddebiti: number;
  movimenti: MovimentoRic[];
}

export const rateMancantiQuery = {
  queryKey: ['admin', 'riconciliazione', 'rate-mancanti'] as const,
  queryFn: () => api.get<{ items: RateMancantiRic[] }>('/admin/riconciliazione/rate-mancanti'),
  staleTime: 60_000,
};

/** Conti (Qonto + cassa) per i select dei popover. */
export const accountsQuery = {
  queryKey: ['admin', 'accounts'] as const,
  queryFn: async (): Promise<AdminAccount[]> => {
    const r = await api.get<AdminAccount[] | { items: AdminAccount[] }>('/admin/accounts');
    return Array.isArray(r) ? r : (r?.items ?? []);
  },
  staleTime: 60_000,
};

// ── Sync / integrazione Qonto ───────────────────────────────────────────────

export interface QontoStatus { connected: boolean; organizationName?: string | null; login?: string | null; connectedAt?: string | null; lastSyncAt?: string | null }

export const qontoStatusQuery = {
  queryKey: ['qonto-status'] as const,
  queryFn: () => api.get<QontoStatus>('/settings/integrations/qonto/status'),
  refetchOnWindowFocus: false,
  retry: 1,
};

// ── Etichette (copy italiano, parole che si usano davvero) ──────────────────

/** Metodi proponibili nei popover (COMPENSAZIONE arriva solo da FattureInCloud: si mostra, non si sceglie). */
export const METHODS: PayMethod[] = ['BONIFICO', 'CONTANTI', 'STRIPE', 'CARTA', 'ADDEBITO', 'RIBA', 'ALTRO'];
/**
 * Come è stata pagata: parole intere ("Ricevuta bancaria", non "RiBa";
 * "Carta online (Stripe)", non "Stripe"). Una lista sola, in @masamune/shared:
 * METODO_LABEL di fatture/fattureApi.ts è la stessa.
 */
export const METHOD_LABEL: Record<PayMethod, string> = ADMIN_PAY_METHOD_LABEL;

/** Che cosa è una riga delle uscite: il nome della cosa, non la tabella da cui viene. */
export const SOURCE_LABEL: Record<UscitaSource, string> = {
  PAYABLE: 'Fattura di un fornitore', RECURRING: 'Spesa fissa', TAX: 'Tassa', CASH: 'Spesa in contanti',
};

/**
 * Etichetta della fonte di una cifra fiscale (Stima, Calcolata da noi,
 * Confermata, Da FattureInCloud): una sola lista, quella di @masamune/shared,
 * così "CALCOLATO" non compare più scritto in maiuscolo da nessuna parte.
 */
export const TAX_SOURCE_LABEL: Record<TaxSource, string> = SHARED_TAX_SOURCE_LABEL;

// ── Date ────────────────────────────────────────────────────────────────────

const DAY_MS = 86400000;
const pad2 = (n: number) => String(n).padStart(2, '0');

/** YYYY-MM-DD locale di oggi (o di `d`). */
export function isoDay(d: Date = new Date()): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** YYYY-MM-DD di oggi + n giorni. */
export function isoDayPlus(days: number): string {
  return isoDay(new Date(Date.now() + days * DAY_MS));
}

/** Primo giorno del mese corrente spostato di `offset` mesi (YYYY-MM-DD). */
export function monthStart(offset = 0): string {
  const d = new Date();
  return isoDay(new Date(d.getFullYear(), d.getMonth() + offset, 1));
}

/** Ultimo giorno del mese corrente spostato di `offset` mesi (YYYY-MM-DD). */
export function monthEnd(offset = 0): string {
  const d = new Date();
  return isoDay(new Date(d.getFullYear(), d.getMonth() + offset + 1, 0));
}

/** Giorni tra oggi e una data (negativo = passata), null senza data. Le date contabili sono date-only. */
export function daysFromToday(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const k = iso.slice(0, 10);
  const [y, m, d] = k.split('-').map(Number);
  if (!y || !m || !d) return null;
  const [ty, tm, td] = isoDay().split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty!, tm! - 1, td!)) / DAY_MS);
}

/**
 * "1 rata" / "7 rate" — il numero con il suo sostantivo al numero giusto.
 *
 * L'Amministrazione conta di continuo (rate, uscite, movimenti, proposte) e
 * metà dei contatori scriveva "1 rate". La regola c'era già, scritta a mano in
 * quattro punti (`rat${n === 1 ? 'a' : 'e'}`): qui sta una volta sola, così
 * nessun contatore nuovo può dimenticarsene.
 *
 *   plurale(1, 'rata', 'rate')  →  "1 rata"
 *   plurale(0, 'rata', 'rate')  →  "0 rate"   (zero vuole il plurale, in italiano)
 */
export function plurale(n: number, uno: string, molti: string): string {
  return `${n} ${n === 1 ? uno : molti}`;
}

/**
 * Un importo scritto come lo scrive un italiano (26/10/2026): "1.300,00", "1300,5", "1300.50",
 * "€ 1.300". Prima i modali di contanti, fatture dei fornitori e spese fisse leggevano
 * "1.300,00" come NaN e il tasto restava spento senza dire perché.
 * Con la virgola, i punti sono le migliaia; senza virgola, un punto solo seguito da una o due
 * cifre è il decimale ("1300.50"), altrimenti sono migliaia ("1.300").
 */
export function leggiImporto(s: string | number | null | undefined): number {
  const t = String(s ?? '').replace(/[€\s]/g, '').trim();
  if (!t) return NaN;
  if (t.includes(',')) return Number(t.replace(/\./g, '').replace(',', '.'));
  if (/^\d+\.\d{1,2}$/.test(t)) return Number(t);
  return Number(t.replace(/\./g, ''));
}

/** "Settembre 2026" da 'YYYY-MM'. */
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const s = new Date(y, m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "ott 2026" corto per le tabelle strette. */
export function monthShort(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('it-IT', { month: 'short', year: 'numeric' });
}

// ── Permessi ────────────────────────────────────────────────────────────────

/** Livello dell'utente corrente sul modulo AMMINISTRAZIONE (hook-free: usabile ovunque). */
export function adminLevel(): PermissionLevel {
  const user = useAuthStore.getState().user;
  return user?.permissions.find((p) => p.module === ModuleKey.AMMINISTRAZIONE)?.level ?? PermissionLevel.NONE;
}

export function canReadAdmin(user: { permissions: Array<{ module: string; level: string }> } | null | undefined): boolean {
  if (!user) return false;
  const lvl = (user.permissions.find((p) => p.module === ModuleKey.AMMINISTRAZIONE)?.level ?? PermissionLevel.NONE) as PermissionLevel;
  return hasAtLeast(lvl, PermissionLevel.READ);
}

export function canWriteAdmin(user: { permissions: Array<{ module: string; level: string }> } | null | undefined): boolean {
  if (!user) return false;
  const lvl = (user.permissions.find((p) => p.module === ModuleKey.AMMINISTRAZIONE)?.level ?? PermissionLevel.NONE) as PermissionLevel;
  return hasAtLeast(lvl, PermissionLevel.WRITE);
}

/** Le righe PERSONALE_ANDREA compaiono solo al Super Admin. */
export function isSuperAdmin(user: { role: { key: string | null } } | null | undefined): boolean {
  return user?.role.key === 'SUPER_ADMIN';
}
